import { FC, useCallback, useMemo } from "react"
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  useTheme,
  CardProps,
  CardActions,
  IconButton,
  Box,
} from "@mui/material"
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded"
import { deepmerge } from "@mui/utils"
import { useNavigate } from "react-router-dom"
import { get } from "lodash"

import { IFeature } from "interfaces"

import { GTM_VIEW_POST } from "constant"

import { getPostDetail, IPostDetail } from "utils/feature"
import { getIcon } from "utils/icon"
import { pushDataLayer } from "utils/googletagmanager"

import { PostData } from "providers/venue/types"
import { useTranslation } from "providers/i18n"

export interface ISeeAndDoCardProps extends CardProps {
  data: PostData
}

export const SeeAndDoCard: FC<ISeeAndDoCardProps> = ({
  data,
  sx = {},
  ...props
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const theme = useTheme()
  const { styleOverrides = {} } = theme.components?.[`SeeAndDoCard`] || {}
  const {
    root,
    cardMedia,
    cardContent,
    descriptionText,
    floorText,
    venueText,
    locationContainer,
    separator,
    button,
    titleText,
    cardActions,
  } = styleOverrides
  const {
    locationUrl,
    coverImage,
    title,
    description,
    type,
    id,
    level,
    category,
    locationVenueRef,
    locationVenueName,
    hasLocation,
  } = getPostDetail(data as unknown as IFeature) as IPostDetail

  const icon = useMemo(
    () =>
      getIcon(theme?.assets?.seeAndDoCardIcon) || <ChevronRightRoundedIcon />,
    [theme]
  )

  const handleClick = useCallback(() => {
    if (!locationUrl) return
    pushDataLayer({
      event: GTM_VIEW_POST,
      post_type: type,
      post_category: get(category, "en"),
      post_id: id,
      post_title: get(title, "en"),
    })

    navigate(locationUrl)
  }, [type, id, title, category, locationUrl, navigate])

  const isRtl = theme.isRtl

  return (
    <Card sx={deepmerge(root, sx)} {...props} onClick={handleClick}>
      <CardMedia sx={cardMedia} image={coverImage} />
      <CardContent sx={cardContent}>
        <Typography variant="h5" component="div" sx={titleText}>
          {t(title)}
        </Typography>
        <Typography variant="body2" sx={descriptionText}>
          {t(description)}
        </Typography>
        {hasLocation && (
          <CardActions sx={cardActions}>
            <Box sx={locationContainer}>
              <Typography
                variant="body2"
                sx={floorText}
                className={locationVenueRef}
              >
                {t(level)}
              </Typography>
              <Typography variant="body2" component="span" sx={separator}>
                •
              </Typography>
              <Typography variant="body2" sx={venueText}>
                {t(locationVenueName)}
              </Typography>
            </Box>

            {locationUrl && (
              <IconButton sx={button} className={isRtl ? "is-rtl" : ""}>
                {icon}
              </IconButton>
            )}
          </CardActions>
        )}
      </CardContent>
    </Card>
  )
}
